import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import PremiumSelect from './PremiumSelect';
import Logo from './raffle/Logo';
import '../css/login.css';

const CustomDropdown = ({ value, onChange, options, placeholder, iconName, type = 'default', textAlign = 'left', showCheckmark = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const triggerClass = type === 'lang' ? 'custom-dropdown-trigger lang-trigger' : 'custom-dropdown-trigger';

  const triggerTextStyle =
    type === 'lang'
      ? {
          color: value ? 'inherit' : 'rgba(0,0,0,0.4)',
          textAlign,
          marginLeft: textAlign === 'center' ? '-12px' : '0',
        }
      : {
          color: value ? 'inherit' : 'rgba(0,0,0,0.4)',
          textAlign: 'left',
        };

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <div className={`${triggerClass} ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        {type === 'lang' ? (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flex: 1,
                minWidth: 0,
                justifyContent: textAlign === 'center' ? 'center' : 'flex-start',
              }}
            >
              <span className="trigger-text" style={triggerTextStyle}>
                {value || placeholder}
              </span>
            </div>
            <Icon
              name="chevron-down"
              size={12}
              style={{
                transition: 'transform 0.3s ease',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                opacity: 0.6,
                marginLeft: '8px',
              }}
            />
          </>
        ) : (
          <>
            <div className="custom-dropdown-field-icon" aria-hidden>
              <Icon name={iconName || 'shield'} size={20} style={{ opacity: 0.8 }} />
            </div>
            <span className="trigger-text" style={triggerTextStyle}>
              {value || placeholder}
            </span>
            <div className={`custom-dropdown-chevron-wrap ${isOpen ? 'is-open' : ''}`}>
              <Icon name="chevron-down" size={12} style={{ opacity: 0.6 }} />
            </div>
          </>
        )}
      </div>
      
      {isOpen && (
        <div className="custom-dropdown-menu">
          {options.map((opt) => (
            <div 
              key={opt.value} 
              className={`dropdown-item ${value === opt.label ? 'active' : ''} text-${textAlign}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {/* Boxed items use icons instead of checkmarks for roles */}
              {type !== 'lang' && <Icon name={opt.icon || iconName || "shield"} size={16} />}
              
              <span>{opt.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Login = ({ onLogin, error, allowedRoles }) => {
  const { t, i18n } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [theme, setTheme] = useState(() =>
    localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'
  );

  // Тёмная тема доступна прямо на экране логина: применяем выбор к <html>
  // и сохраняем в localStorage, чтобы он держался и после входа.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const languages = [
    { value: 'ru', label: 'Рус' },
    { value: 'en', label: 'Eng' },
    { value: 'ro', label: 'Rom' },
  ];

  // Map roles to specific icons
  const getRoleIcon = (roleVal) => {
    if (roleVal === allowedRoles.DEV) return 'zap';
    if (roleVal === allowedRoles.ADMIN) return 'shield';
    if (roleVal === allowedRoles.MODERATOR) return 'activity';
    if (roleVal === allowedRoles.SUPPORT) return 'users';
    return 'user';
  };

  const roleOptions = Object.entries(allowedRoles).map(([key, val]) => ({
    value: val,
    label: val,
    icon: getRoleIcon(val)
  }));

  const currentLangLabel = languages.find(l => l.value === i18n.language)?.label || 'Рус';
  const currentRoleIcon = getRoleIcon(role);

  useEffect(() => {
    const savedPhone = localStorage.getItem('lastPhoneNumber');
    const savedPassword = localStorage.getItem('lastPassword');
    const savedRole = localStorage.getItem('lastRole');
    const savedRemember = localStorage.getItem('rememberLogin') === 'true';
    if (savedPhone) setPhoneNumber(savedPhone);
    if (savedRemember) {
      if (savedPassword) setPassword(savedPassword);
      if (savedRole && Object.values(allowedRoles).includes(savedRole)) {
        setRole(savedRole);
      }
    }
    setRememberMe(savedRemember);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (phoneNumber && password && role) {
      if (rememberMe) {
        localStorage.setItem('lastPhoneNumber', phoneNumber);
        localStorage.setItem('lastPassword', password);
        localStorage.setItem('lastRole', role);
        localStorage.setItem('rememberLogin', 'true');
      } else {
        localStorage.setItem('lastPhoneNumber', phoneNumber);
        localStorage.removeItem('lastPassword');
        localStorage.removeItem('lastRole');
        localStorage.setItem('rememberLogin', 'false');
      }
      onLogin(phoneNumber, password, role);
    }
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <div className="login-container">
      <div className="login-bg-overlay"></div>
      <div className="login-bg-blob blob-1"></div>
      <div className="login-bg-blob blob-2"></div>

      <div className="login-form">
        <div className="login-top">
          <div className="login-brand">
            <Logo className="login-logo-svg" />
          </div>
          <div className="login-top-actions">
            <button
              type="button"
              className="login-theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === 'dark'
                ? t('nav.theme.switchToLight', { defaultValue: 'Светлая тема' })
                : t('nav.theme.switchToDark', { defaultValue: 'Тёмная тема' })}
              title={theme === 'dark'
                ? t('nav.theme.switchToLight', { defaultValue: 'Светлая тема' })
                : t('nav.theme.switchToDark', { defaultValue: 'Тёмная тема' })}
            >
              <Icon name={theme === 'dark' ? 'theme-light' : 'theme-dark'} size={18} />
            </button>
            <div className="login-lang-slot">
              <CustomDropdown
                value={currentLangLabel}
                onChange={handleLanguageChange}
                options={languages}
                type="lang"
                textAlign="center"
                showCheckmark={false}
              />
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} autoComplete="on">
          {error && (
            <div className="error-message">
              <Icon name="alert-circle" size={18} />
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('login.phone')}</label>
            <div className="form-input-wrapper">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="form-input"
                placeholder={t('login.phonePlaceholder')}
                required
                autoFocus
                autoComplete="tel"
              />
              <div className="input-icon-wrapper">
                <Icon name="phone" size={20} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('login.password')}</label>
            <div className="form-input-wrapper">
              <input
                type={isPasswordVisible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                required
                autoComplete="current-password"
                name="password"
              />
              <div className="input-icon-wrapper">
                <Icon name="lock" size={20} />
              </div>
              <button
                type="button"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                className="password-toggle"
                aria-label={isPasswordVisible ? t('login.hidePassword') : t('login.showPassword')}
              >
                <Icon name={isPasswordVisible ? 'eye-off' : 'eye'} size={22} />
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('login.role')}</label>
            <PremiumSelect
              value={role}
              onChange={setRole}
              options={roleOptions}
              placeholder={t('login.rolePlaceholder', { defaultValue: 'Выбрать роль' })}
              placeholderIcon={currentRoleIcon}
            />
          </div>

          <div className="remember-row">
            <label className="remember-checkbox">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>{t('login.remember')}</span>
            </label>
          </div>

          <button type="submit" className="submit-button">
            {t('login.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
